import React, { useEffect, useState } from 'react';
import { adminFetch } from '../../lib/api';
import { Briefcase, Building2, TrendingUp, Users, Euro, Target, ArrowRight, Clock } from 'lucide-react';

interface DashboardData {
  kpis: {
    totalLeads: number;
    openLeads: number;
    wonLeads: number;
    lostLeads: number;
    conversionRate: number;
    pipelineValue: number;
    activePartners: number;
  };
  pipelineByStatus: Record<string, { count: number; value: number }>;
  recentActivities: any[];
  upcomingFollowups: any[];
}

const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu',
  kontaktiert: 'Kontaktiert',
  interessiert: 'Interessiert',
  demo: 'Demo',
  verhandlung: 'Verhandlung',
  gewonnen: 'Gewonnen',
  verloren: 'Verloren',
};

const STATUS_COLORS: Record<string, string> = {
  neu: '#8b95a5',
  kontaktiert: '#3b82f6',
  interessiert: '#f59e0b',
  demo: '#8b5cf6',
  verhandlung: '#ec4899',
  gewonnen: '#10b981',
  verloren: '#ef4444',
};

const ACTIVITY_LABELS: Record<string, string> = {
  call: '📞 Anruf',
  email: '✉️ E-Mail',
  meeting: '🤝 Meeting',
  demo: '📺 Demo',
  note: '📝 Notiz',
  status_change: '🔄 Status',
  follow_up: '🔔 Follow-up',
  linkedin_message: '💼 LinkedIn',
  event: '📅 Event',
  other: '📋 Sonstiges',
};

export const SalesDashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await adminFetch('/api/admin/sales/dashboard');
      const json = await res.json();
      // Ensure we have valid dashboard data structure
      if (json && json.kpis) {
        setData(json);
      } else {
        setData({
          kpis: { totalLeads: 0, openLeads: 0, wonLeads: 0, lostLeads: 0, conversionRate: 0, pipelineValue: 0, activePartners: 0 },
          pipelineByStatus: {},
          recentActivities: [],
          upcomingFollowups: [],
        });
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
      setData({
        kpis: { totalLeads: 0, openLeads: 0, wonLeads: 0, lostLeads: 0, conversionRate: 0, pipelineValue: 0, activePartners: 0 },
        pipelineByStatus: {},
        recentActivities: [],
        upcomingFollowups: [],
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Vertriebsübersicht</h1></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {[1,2,3,4,5,6,7].map(i => <div key={i} className="card skeleton" style={{ height: 100 }} />)}
        </div>
      </div>
    );
  }

  if (!data) return <div className="page-container"><p>Fehler beim Laden</p></div>;

  const kpis = data.kpis;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><Briefcase style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Vertriebsübersicht</h1>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <KpiCard icon={<Building2 />} label="Leads gesamt" value={kpis.totalLeads} color="#3b82f6" />
        <KpiCard icon={<Target />} label="Offene Leads" value={kpis.openLeads} color="#f59e0b" />
        <KpiCard icon={<TrendingUp />} label="Gewonnen" value={kpis.wonLeads} color="#10b981" />
        <KpiCard icon={<Euro />} label="Pipeline-Wert" value={`${(kpis.pipelineValue / 100).toLocaleString('de-DE', { minimumFractionDigits: 0 })} €`} color="#8b5cf6" />
        <KpiCard icon={<Target />} label="Konversionsrate" value={`${kpis.conversionRate}%`} color="#ec4899" />
        <KpiCard icon={<Users />} label="Aktive Partner" value={kpis.activePartners} color="#06b6d4" />
        <KpiCard icon={<Building2 />} label="Verloren" value={kpis.lostLeads} color="#ef4444" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Pipeline Overview */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>📊 Pipeline-Übersicht</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(data.pipelineByStatus).map(([status, info]) => {
              const maxCount = Math.max(...Object.values(data.pipelineByStatus).map(s => s.count), 1);
              return (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 100, fontSize: 13, color: 'var(--text-secondary)' }}>{STATUS_LABELS[status] || status}</span>
                  <div style={{ flex: 1, height: 24, background: 'var(--bg-secondary)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      width: `${(info.count / maxCount) * 100}%`,
                      height: '100%',
                      background: STATUS_COLORS[status] || '#666',
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', paddingLeft: 8,
                      fontSize: 12, color: '#fff', fontWeight: 600,
                      minWidth: info.count > 0 ? 30 : 0,
                      transition: 'width 0.3s ease',
                    }}>
                      {info.count}
                    </div>
                  </div>
                  <span style={{ width: 80, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {(info.value / 100).toLocaleString('de-DE')} €
                  </span>
                </div>
              );
            })}
          </div>
          {Object.keys(data.pipelineByStatus).length === 0 && (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 20 }}>Noch keine Leads vorhanden</p>
          )}
        </div>

        {/* Upcoming Follow-ups */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}><Clock size={16} style={{ marginRight: 6 }} /> Anstehende Follow-ups</h3>
          {data.upcomingFollowups.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 20 }}>Keine anstehenden Follow-ups</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.upcomingFollowups.map((fu: any) => (
                <div key={fu.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, gap: 12,
                }}>
                  <div>
                    <strong style={{ fontSize: 14 }}>{fu.name}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fu.next_action}</div>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--accent-red)', whiteSpace: 'nowrap' }}>
                    {new Date(fu.next_action_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activities */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 16 }}>🕐 Letzte Aktivitäten</h3>
        {data.recentActivities.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 20 }}>Noch keine Aktivitäten</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.recentActivities.map((a: any) => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13,
              }}>
                <span>{ACTIVITY_LABELS[a.activity_type] || a.activity_type}</span>
                <ArrowRight size={12} />
                <span style={{ flex: 1 }}>{a.title}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                  {new Date(a.performed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; color: string }> = ({ icon, label, value, color }) => (
  <div className="card" style={{ padding: '20px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{
      width: 42, height: 42, borderRadius: 10,
      background: `${color}15`, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {React.cloneElement(icon as React.ReactElement<any>, { size: 20 })}
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);
